import { Component, computed, signal } from '@angular/core';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DividerModule } from 'primeng/divider';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MessagesModule } from 'primeng/messages';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CheckboxModule } from 'primeng/checkbox';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

import { WaitDaysNode } from '@modules/campaigns/Domain/Nodes/WaitDaysNode';
import { WaitDaysConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { InspectorBase } from '@shared/components/joint-js/components/inspectors/inspector-base';
import { RegionalConfigService } from '@admin/modules/regional-configs/services/regional-configs.service';
import { RegionalConfigsModel } from '@admin/modules/regional-configs/model/regional-configs.model';
import { convertHHMMToDate, formatTimeToHHMM } from '@shared/helpers/standarFormat';
import { CampaignContextService } from '@shared/services/campaign-context.service';
import { CampaignModel } from '@modules/campaigns/models/campaign.model';
import { RegionalConfigInfoComponent } from '@shared/components/regional-config-info/regional-config-info.component';

type Interval = {
  start: Date | null;
  finish: Date | null;
};

@Component({
  selector: 'app-wait-days-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    TranslateModule,
    DividerModule,
    SelectButtonModule,
    InputNumberModule,
    InputSwitchModule,
    MessagesModule,
    DropdownModule,
    CalendarModule,
    RadioButtonModule,
    CheckboxModule,
    CardModule,
    ButtonModule,
    TooltipModule,
    RegionalConfigInfoComponent,
  ],
  templateUrl: './wait-days-inspector.component.html',
  styleUrls: ['./wait-days-inspector.component.scss'],
})
export class WaitDaysInspectorComponent extends InspectorBase<WaitDaysNode> {
  waitDays = signal<number | null>(0);
  partDay = signal<string | null>('MORNING');
  customizeTimeRange = signal<boolean>(false);
  interval = signal<Interval | null>({
    start: new Date(),
    finish: new Date(new Date().getTime() + 60 * 60 * 1000),
  });

  regionalConfigs = signal<RegionalConfigsModel[]>([]);
  campaignSelected = signal<CampaignModel | null>(null);
  rangeErrors = signal<{ invalidOrder?: boolean; overlaps?: boolean }>({});

  partDayOptions = computed(() => [
    {
      label: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.MORNING'),
      value: 'MORNING',
    },
    {
      label: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.EVENING'),
      value: 'EVENING',
    },
    {
      label: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.FULL_DAY'),
      value: 'FULL_DAY',
    },
  ]);

  groupedCollectors = computed(() => {
    const campaign = this.campaignSelected();
    const configs = this.regionalConfigs();
    if (!campaign?.collectorIds || !configs.length) return [];

    return this.groupCollectorsByRegionalConfigs(campaign.collectorIds, configs);
  });

  formattedTimeRange = computed(() => {
    const interval = this.interval();
    if (!interval?.start || !interval?.finish) return '';
    return `${formatTimeToHHMM(interval.start)} - ${formatTimeToHHMM(interval.finish)}`;
  });

  constructor(
    private workflow: WorkflowStateService,
    private translate: TranslateService,
    private campaignContext: CampaignContextService,
  ) {
    super();
  }

  override get nodeValue(): WaitDaysNode | null {
    const current = this.node();
    if (!current) return null;
    return current.type === NodeType.WaitDays ? (current as WaitDaysNode) : null;
  }

  getTabs() {
    return [
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.WAIT_DAYS.TABS.PREVIEW'),
        value: 'preview' as const,
      },
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.WAIT_DAYS.TABS.CONFIG'),
        value: 'config' as const,
      },
    ];
  }

  override setNode(nodeSignal: any) {
    super.setNode(nodeSignal);
    this.loadCampaignContext();
  }

  private loadCampaignContext() {
    this.campaignContext.campaign$.subscribe((campaign) => {
      if (campaign) {
        this.campaignSelected.set(campaign);
      }
    });
  }

  onPartDayChange(value: string) {
    this.partDay.set(value);
    this.scheduleApplyChanges();
  }

  onWaitDaysChange(value: number | null) {
    if (value == null || isNaN(value as any)) value = 1;
    if (value < 0) value = 0;
    if (value > 365) value = 365;

    this.waitDays.set(value);
    this.scheduleApplyChanges();
  }

  onCustomizeTimeRangeChange(value: boolean) {
    this.customizeTimeRange.set(value);
    if (!value) {
      this.interval.set({
        start: new Date(),
        finish: new Date(new Date().getTime() + 60 * 60 * 1000),
      });
    }
    this.scheduleApplyChanges();
  }

  onTimeRangeChange(key: 'start' | 'finish', value: Date | null) {
    const current = this.interval();
    if (!current) return;

    const updated = { ...current, [key]: value };
    this.interval.set(updated);

    this.validateInterval();
    this.scheduleApplyChanges();
  }

  //TODO: change
  private validateInterval() {
    const errors: { invalidOrder?: boolean; overlaps?: boolean } = {};
    const interval = this.interval();

    if (!interval?.start || !interval?.finish) {
      this.rangeErrors.set(errors);
      return;
    }

    const start = interval.start.getTime();
    const finish = interval.finish.getTime();

    if (start >= finish) {
      errors.invalidOrder = true;
    }

    this.rangeErrors.set(errors);
  }

  private groupCollectorsByRegionalConfigs(
    campaignCollectors: string[],
    regionalConfigs: RegionalConfigsModel[],
  ): any[] {
    const result: any[] = [];
    regionalConfigs.forEach((config) => {
      const { timezone, timetable, collectors = [] } = config;

      const matchedCollectors = collectors.filter((c: any) => campaignCollectors.includes(c.id));

      if (matchedCollectors.length) {
        result.push({
          timezone,
          collectors: matchedCollectors,
          timetable: {
            MORNING: timetable?.MORNING || null,
            EVENING: timetable?.EVENING || null,
            FULL_DAY: timetable?.FULL_DAY || null,
          },
        });
      }
    });

    return result;
  }

  getTimetableEntries(timetable: any) {
    if (!timetable) return [];

    const order = ['MORNING', 'EVENING', 'FULL_DAY'];

    return Object.entries(timetable)
      .map(([key, value]: [string, any]) => ({
        key,
        label: this.mapPeriodLabel(key),
        start: value?.start,
        finish: value?.finish || value?.end,
      }))
      .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  }

  private mapPeriodLabel(key: string): string {
    const labels: { [key: string]: string } = {
      MORNING: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.MORNING'),
      EVENING: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.EVENING'),
      FULL_DAY: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.FULL_DAY'),
    };
    return labels[key] || key;
  }

  getSummaryMessage(): string {
    const timeRange = this.getFormattedTimeRange().toLowerCase();
    const waitDays = this.waitDays() ?? 0;

    return this.translate.instant('DATA.MODULES.INSPECTORS.WAIT_DAYS.PREVIEW.SUMMARY_MESSAGE', {
      timeRange,
      waitDays,
    });
  }

  private getFormattedTimeRange(): string {
    if (this.customizeTimeRange()) {
      return this.formattedTimeRange() || this.getPartDayLabel();
    } else {
      return this.getPartDayLabel();
    }
  }

  private getPartDayLabel(): string {
    const partDay = this.partDay();
    if (partDay === 'MORNING') {
      return this.translate.instant('DATA.COMMON.TIME_OF_WEEK.MORNING');
    } else if (partDay === 'EVENING') {
      return this.translate.instant('DATA.COMMON.TIME_OF_WEEK.EVENING');
    } else if (partDay === 'FULL_DAY') {
      return this.translate.instant('DATA.COMMON.TIME_OF_WEEK.FULL_DAY');
    }
    return '-';
  }

  override applyChanges() {
    if (this.mode() === 'preview') return;

    const node = this.nodeValue;
    if (!node) return;

    const updatedConfig: WaitDaysConfig = {
      ...(node.config as WaitDaysConfig),
      partDay: this.partDay() || null,
      waitDays: this.waitDays() ?? 0,
    };

    if (this.customizeTimeRange() && !this.rangeErrors().invalidOrder) {
      const interval = this.interval();
      if (interval?.start && interval?.finish) {
        const startStr = formatTimeToHHMM(interval.start);
        const finishStr = formatTimeToHHMM(interval.finish);
        if (startStr && finishStr) {
          updatedConfig.interval = {
            start: startStr,
            finish: finishStr,
          };
        }
      }
    } else {
      updatedConfig.interval = null;
    }

    node.config = updatedConfig;
    const validation = node.validate();
    this.errors.set(validation);

    if (!validation.length) {
      this.workflow.addOrUpdateNode(node);
    }
  }

  override refreshFromConfig() {
    const node = this.nodeValue;
    if (!node) return;

    const cfg = (node.config ?? {}) as WaitDaysConfig;

    this.partDay.set(cfg.partDay ?? 'MORNING');
    this.waitDays.set(cfg.waitDays ?? 1);

    const rawInterval = cfg.interval;
    if (rawInterval) {
      this.interval.set({
        start: convertHHMMToDate(rawInterval.start),
        finish: convertHHMMToDate(rawInterval.finish),
      });
      this.customizeTimeRange.set(true);
    } else {
      this.interval.set({
        start: new Date(),
        finish: new Date(new Date().getTime() + 60 * 60 * 1000),
      });
      this.customizeTimeRange.set(false);
    }

    this.validateInterval();
    this.errors.set([]);
  }
}
